import axios from 'axios';
import buildPaginationQueryOpts from '@/shared/sort/sorts';
import { BASE_API_URL } from '@/constants';
import { PREFIX_API_PUBLIC } from '@/constants';

import { IExamSession } from '@/shared/model/exam-session.model';

const entityName = 'exam-sessions';

export default class ExamSessionService {
  /**
   * Retrieve an examSession by its ID
   * @param id  selected examSession ID
   */
  public find(id: number): Promise<IExamSession> {
    return new Promise<IExamSession>((resolve, reject) => {
      axios
        .get(`${BASE_API_URL}${entityName}/${id}`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  /**
   * Retrieve all examSessions
   * @param paginationQuery
   */
  public retrieve(paginationQuery?: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      axios
        .get(BASE_API_URL + entityName + `?${buildPaginationQueryOpts(paginationQuery)}`)
        .then(res => {
          resolve(res);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  /**
   * Retrieve all examSession for one examCenter
   * @param examCenterId  selected examCenter ID
   * @param paginationQuery
   */
  public retrieveByExamCenter(examCenterId: number, paginationQuery?: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      axios
        .get(BASE_API_URL + entityName + `ByExamCenter/${examCenterId}?${buildPaginationQueryOpts(paginationQuery)}`)
        .then(res => {
          resolve(res);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  /**
   * Retrieve all examSessions available for registration, filtered by SR (optionnal)
   * @param paginationQuery
   */
  public retrieveAllAvailable(idSr?: number): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      let url = BASE_API_URL + entityName + `/all-available`;
      if (idSr !== undefined) {
        url += `?srId=${idSr}`;
      }
      axios
        .get(url)
        .then(res => {
          resolve(res);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  /**
   * Retrieve all examSessions by criteria
   * @param latitude
   * @param longitude
   * @param examType
   * @param examDate
   */
  public retrieveByCriteria(latitude: number, longitude: number, examType: string, examDate: string): Promise<any> {
    let searchParams = `/search?lat=${latitude}&long=${longitude}&type=${examType}&date=${examDate}`;
    return new Promise<any>((resolve, reject) => {
      axios
        .get(BASE_API_URL + PREFIX_API_PUBLIC + entityName + searchParams)
        .then(res => {
          resolve(res);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  /**
   * Export all registrations for an examSession as CSV file for Code Rousseau
   * @param examSession   selected examSession ID
   */
  public exportInscriptionsBySessionAsCsv(examSession: IExamSession) {
    return new Promise<any>((resolve, reject) => {
      axios
        .post(`${BASE_API_URL}${entityName}/export-inscriptions-by-examSession`, examSession, { responseType: 'blob' })
        .then(res => {
          resolve(res);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  /**
   * Delete an examSession
   * @param id  examSession to remove ID
   */
  public delete(id: number): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      axios
        .delete(`${BASE_API_URL}${entityName}/${id}`)
        .then(res => {
          resolve(res);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  /**
   * Create new examSession
   * @param entity  examSession to create
   */
  public create(entity: IExamSession): Promise<IExamSession> {
    return new Promise<IExamSession>((resolve, reject) => {
      axios
        .post(`${BASE_API_URL}${entityName}`, entity)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  /**
   * Update an examSession
   * @param entity  examSession to update
   */
  public update(entity: IExamSession): Promise<IExamSession> {
    return new Promise<IExamSession>((resolve, reject) => {
      axios
        .put(`${BASE_API_URL}${entityName}`, entity)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }
}
